<script setup>
import {onMounted, ref} from 'vue';
import {useDataTable} from '@/utils/datatableHelper';
import {useToast} from "vue-toastification";
import MeetingRecordService from '@/services/MeetingRecordService';
import {formatDate, showSuccessMessage} from '@/helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import CreateEditMeetingRecordModal from '@/components/modals/CreateEditMeetingRecordModal.vue';
import ModalDialog from '@/components/modals/ModalDialog.vue';

import {useI18n} from 'vue-i18n'
import {authData} from '@/services/AuthService';

const {t} = useI18n()

const props = defineProps({
  clientId: Number
});

const toast = useToast();

const isLoading = ref(false);
const datatable = useDataTable(fetchRecords, 'created_at', 10, true);
const rows = ref([]);
const columns = ref([
  {label: t('meetingRecordsIndex.date'), field: 'created_at', sortable: false, filterOptions: {enabled: false}, formatFn: (value) => formatDate(value, 'd.m.Y H:i')},
  {label: t('meetingRecordsIndex.status'), field: 'state', sortable: false},
  {label: t('meetingRecordsIndex.action'), field: 'action', sortable: false},
]);

function fetchRecords(body) {
    body = {...body,...{filter: {client_id: props.clientId}}};
  MeetingRecordService.fetchRecords(props.clientId, body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    rows.value = response.data;
  });
}

function viewMeetingRecord(meetingRecord) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateEditMeetingRecordModal, {meetingRecord, disabled: meetingRecord.state !== 'CONCEPT'});
  reveal();
  onConfirm((data) => {
    data.client_id = props.clientId;
    data.creator_id = authData.loggedUser.id;
    MeetingRecordService.saveMeetingRecord(props.clientId, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('meetingRecordSaved');
      datatable.fetchWithValues();
    });
  });
  onCancel(() => {
  });
}

function deleteMeetingRecord(meetingRecord) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('meetingRecordsIndex.meeting_records_delete'),
    message: t('meetingRecordsIndex.confirm_delete_entry'),
  });
  onConfirm(() => {
    MeetingRecordService.deleteRecord(props.clientId, meetingRecord.id).then(() => {
      showSuccessMessage(t('meetingRecordsIndex.meeting_records_deleted'));
      datatable.fetchWithValues();
    });
  });
  reveal();
}

onMounted(() => {
  emitter.on('meetingRecordSaved', () => {
    datatable.fetchWithValues();
  });
})
</script>

<template>
  <div class="page-section">
    <div class="page-section__content">
      <div class="mt-3">
        <vue-good-table
            :columns="columns"
            :rows="rows"
            mode="remote"
            @page-change="datatable.onPageChange"
            @sort-change="datatable.onSortChange"
            @column-filter="datatable.onColumnFilter"
            @per-page-change="datatable.onPerPageChange"
            :totalRows="datatable.totalRecords.value"
            :pagination-options="{
                  enabled: true,
                  rowsPerPageLabel: t('meetingRecordsIndex.entriesPerPage'),
                  nextLabel: t('meetingRecordsIndex.next'),
                  prevLabel: t('meetingRecordsIndex.previous'),
                  ofLabel: 'z',
                }">
          <template #table-row="props">
            <span v-if="props.column.field === 'state'">
            <span v-if="props.row.state === 'DENIED'"> {{ t('meetingRecordsIndex.state_rejected') }} </span>
            <span v-if="props.row.state === 'APPROVED'"> {{ t('meetingRecordsIndex.state_confirmed') }} </span>
            <span v-if="props.row.state === 'WAITING'"> {{ t('meetingRecordsIndex.state_waiting_confirm') }} </span>
              <span v-if="props.row.state === 'TO_REVIEW'"> {{ t('meetingRecordsIndex.state_to_review') }} </span>
            <span v-if="props.row.state === 'CANCELED'"> {{ t('meetingRecordsIndex.state_canceled') }} </span>
            <span v-if="props.row.state === 'ACCEPTED'"> {{ t('meetingRecordsIndex.state_accepted') }} </span>
            <span v-if="props.row.state === 'CONCEPT'"> {{ t('meetingRecordsIndex.state_concept') }} </span>
          </span>
          <span v-if="props.column.field === 'action'">
            <span style="font-weight: bold; float: right">
              <button type="button" class="btn btn-link"><i @click="viewMeetingRecord(props.row)" class="mdi mdi-eye"/></button>
              <button type="button" class="btn btn-error" @click="deleteMeetingRecord(props.row)"><i class="mdi mdi-trash-can-outline"/></button>
            </span>
          </span>
          </template>
          <template v-slot:emptystate v-if="!isLoading">
            <div class="text-center">
              {{ t('meetingRecordsIndex.no_records_found') }}
            </div>
          </template>
          <template v-slot:emptystate v-if="isLoading">
            <div class="text-center">
              {{ t('meetingRecordsIndex.data_loading') }}
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>
